import { useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { Loader2, Minus, Plus } from 'lucide-react';

import { formatKop, formatQty } from '@/lib/format';

export function Spinner({ label, className }: { label?: string; className?: string }) {
  return (
    <div className={clsx('flex items-center justify-center gap-2 py-16 text-ink/50', className)}>
      <Loader2 className="animate-spin" size={18} />
      {label && <span className="text-sm">{label}</span>}
    </div>
  );
}

/** Шапка экрана: заголовок, подпись, действия справа. Липнет к верху под чёлкой. */
export function ScreenHeader({ title, subtitle, left, right }: { title: ReactNode; subtitle?: ReactNode; left?: ReactNode; right?: ReactNode }) {
  return (
    <header className="pt-safe sticky top-0 z-20 border-b border-line bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/85">
      <div className="mx-auto flex h-14 max-w-md items-center gap-1 px-2">
        {left ?? <span className="w-2" />}
        <div className="min-w-0 flex-1 px-1">
          <h1 className="truncate text-[17px] font-semibold leading-tight text-ink">{title}</h1>
          {subtitle != null && <div className="truncate text-xs text-ink/50">{subtitle}</div>}
        </div>
        {right}
      </div>
    </header>
  );
}

export function Stat({ label, value, tone }: { label: string; value: ReactNode; tone?: 'red' | 'green' }) {
  return (
    <div className="min-w-0">
      <div className="truncate text-[12px] text-ink/50">{label}</div>
      <div className={clsx('truncate text-[15px] font-semibold tabular-nums', tone === 'red' ? 'text-red-600' : tone === 'green' ? 'text-emerald-700' : 'text-ink')}>
        {value}
      </div>
    </div>
  );
}

/** Долг точки: плюс — точка должна, минус — переплата. */
export function DebtText({ value, className }: { value: number; className?: string }) {
  if (value === 0) return <span className={clsx('tabular-nums text-ink/40', className)}>0 ₽</span>;
  return (
    <span className={clsx('tabular-nums', value > 0 ? 'text-red-600' : 'text-emerald-700', className)}>
      {value < 0 ? '−' : ''}
      {formatKop(Math.abs(value))}
    </span>
  );
}

/** Изменение долга: «+400 ₽» — вырос, «−1 200 ₽» — собрали. */
export function DebtDelta({ value }: { value: number }) {
  if (value === 0) return <span className="tabular-nums text-ink/40">—</span>;
  return (
    <span className={clsx('tabular-nums', value > 0 ? 'text-red-600' : 'text-emerald-700')}>
      {value > 0 ? '+' : '−'}
      {formatKop(Math.abs(value))}
    </span>
  );
}

/** Счётчик количества: крупные кнопки под палец, число можно ввести руками. */
export function Stepper({
  value,
  onChange,
  max,
  step = 1,
  fractional,
  tone = 'default',
  label,
  disabled,
}: {
  value: number;
  onChange: (v: number) => void;
  max?: number;
  step?: number;
  fractional?: boolean;
  tone?: 'default' | 'return';
  label: string;
  disabled?: boolean;
}) {
  const clamp = (v: number) => {
    const rounded = fractional ? Math.round(v * 1000) / 1000 : Math.round(v);
    return Math.max(0, max != null ? Math.min(rounded, max) : rounded);
  };
  const active = value > 0;
  // Пока поле в фокусе — показываем то, что набрано: иначе «1,» сразу
  // превращалось бы в «1» и дробь не ввести.
  const [text, setText] = useState<string | null>(null);
  return (
    <div
      className={clsx(
        'flex h-11 items-center rounded-xl border transition-colors',
        disabled ? 'border-line bg-paper opacity-60' : active ? (tone === 'return' ? 'border-amber-300 bg-amber-50' : 'border-orange/40 bg-orange-50') : 'border-line bg-white',
      )}
    >
      <button
        type="button"
        className="flex h-full w-11 shrink-0 items-center justify-center text-ink/60 active:text-ink disabled:opacity-30"
        onClick={() => onChange(clamp(value - step))}
        disabled={disabled || value <= 0}
        aria-label={`${label}: меньше`}
      >
        <Minus size={18} />
      </button>
      <input
        className="h-full w-full min-w-0 bg-transparent text-center text-[16px] font-semibold tabular-nums text-ink outline-none"
        inputMode={fractional ? 'decimal' : 'numeric'}
        value={text ?? (value === 0 ? '' : formatQty(value).replace(/\s/g, ''))}
        placeholder="0"
        disabled={disabled}
        aria-label={label}
        onFocus={(e) => {
          setText(value === 0 ? '' : formatQty(value).replace(/\s/g, ''));
          e.currentTarget.select();
        }}
        onBlur={() => setText(null)}
        onChange={(e) => {
          const raw = fractional ? e.target.value.replace(/[^\d.,]/g, '') : e.target.value.replace(/\D/g, '');
          setText(raw);
          const n = Number(raw.replace(',', '.'));
          onChange(Number.isFinite(n) ? clamp(n) : 0);
        }}
      />
      <button
        type="button"
        className="flex h-full w-11 shrink-0 items-center justify-center text-ink/60 active:text-ink disabled:opacity-30"
        onClick={() => onChange(clamp(value + step))}
        disabled={disabled || (max != null && value >= max)}
        aria-label={`${label}: больше`}
      >
        <Plus size={18} />
      </button>
    </div>
  );
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  className,
}: {
  value: T;
  onChange: (v: T) => void;
  options: ReadonlyArray<{ value: T; label: ReactNode }>;
  className?: string;
}) {
  return (
    <div className={clsx('seg no-scrollbar', className)} role="group">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          className={clsx('seg-item flex-1', o.value === value && 'seg-item-active')}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
